import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowRight, Building2, Lock, Instagram, Twitter, Facebook } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3500);
    }
  };

  return (
    <footer className="bg-ink-900 text-cream-100">
      {/* Newsletter Strip */}
      <div className="border-b border-ink-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <span className="text-eyebrow text-copper-400 font-mono">— Stay in focus</span>
              <h3 className="font-display text-4xl md:text-5xl lg:text-6xl text-cream-50 mt-4 leading-[1.05]">
                Join the <span className="italic font-light text-copper-400">Clarity</span> list.
              </h3>
              <p className="text-cream-200/70 mt-4 max-w-md">
                Early access to new frames, care tips and private offers for subscribers only.
              </p>
            </div>
            <div className="lg:col-span-5">
              <form onSubmit={handleSubscribe} className="flex items-center gap-0 border-b border-cream-200/30 pb-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="flex-1 bg-transparent text-cream-50 placeholder:text-cream-200/40 focus:outline-none text-lg py-2"
                />
                <button type="submit" className="flex items-center gap-2 text-cream-50 hover:text-copper-400 transition-colors group">
                  <span className="text-sm tracking-[0.15em] uppercase font-mono">Subscribe</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
              {subscribed && (
                <p className="text-copper-400 text-sm mt-3 animate-fade-in">✓ Thanks for subscribing.</p>
              )}
              <p className="text-cream-200/40 text-xs mt-3">By subscribing, you agree to our Privacy Policy.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Columns */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand block */}
          <div className="col-span-2 lg:col-span-4">
            <Link to="/" className="inline-block mb-6">
              <span className="font-display text-3xl text-cream-50">
                Clarity <span className="italic font-light">Lens</span> Co
              </span>
            </Link>
            <p className="text-cream-200/60 text-sm leading-relaxed mb-8 max-w-sm">
              Precision-crafted eyewear and vision accessories made for clarity, comfort and everyday elegance.
            </p>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3 text-cream-200/80">
                <Building2 className="w-4 h-4 mt-0.5 text-copper-400 flex-shrink-0" />
                <div>
                  <p className="text-cream-50 font-medium">{CONTACT_INFO.corpName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3 text-cream-200/70">
                <MapPin className="w-4 h-4 mt-0.5 text-copper-400 flex-shrink-0" />
                <address className="not-italic">
                  {CONTACT_INFO.address.street}<br />
                  {CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}
                </address>
              </div>
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-3 text-cream-200/70 hover:text-cream-50 transition-colors">
                <Phone className="w-4 h-4 text-copper-400" />
                {CONTACT_INFO.phone}
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 text-cream-200/70 hover:text-cream-50 transition-colors">
                <Mail className="w-4 h-4 text-copper-400" />
                {CONTACT_INFO.email}
              </a>
            </div>
          </div>

          {/* Shop */}
          <div className="lg:col-span-2 lg:col-start-6">
            <h4 className="text-eyebrow text-copper-400 mb-5 font-mono">Shop</h4>
            <ul className="space-y-3">
              <li><Link to="/shop" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">All Eyewear</Link></li>
              {categories.slice(0, 5).map((cat) => (
                <li key={cat.id}>
                  <Link to={`/shop?category=${cat.slug}`} className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">
                    {cat.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Help */}
          <div className="lg:col-span-2">
            <h4 className="text-eyebrow text-copper-400 mb-5 font-mono">Support</h4>
            <ul className="space-y-3">
              <li><Link to="/faq" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">FAQ</Link></li>
              <li><Link to="/how-to-use" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">How to Use</Link></li>
              <li><Link to="/shipping" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Shipping</Link></li>
              <li><Link to="/returns" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Returns</Link></li>
              <li><Link to="/contact" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h4 className="text-eyebrow text-copper-400 mb-5 font-mono">Company</h4>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">About</Link></li>
              <li><Link to="/why-choose-us" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Why Us</Link></li>
              <li><Link to="/materials" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Materials</Link></li>
              <li><Link to="/guarantee" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Guarantee</Link></li>
              <li><Link to="/privacy" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-cream-200/70 hover:text-cream-50 text-sm transition-colors">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Giant Wordmark */}
      <div className="overflow-hidden border-t border-ink-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 pb-6">
          <div className="flex items-baseline justify-between flex-wrap gap-4">
            <h2 className="font-display leading-[0.85] text-cream-50/[0.07] text-[20vw] md:text-[18vw] lg:text-[15vw] select-none pointer-events-none">
              CLARITY
            </h2>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-ink-800">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-cream-200/50">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
              <p>&copy; {currentYear} Clarity Lens Co.</p>
              <span className="hidden sm:inline">•</span>
              <p className="text-copper-400/80">{CONTACT_INFO.corpName}</p>
              <span className="hidden sm:inline">•</span>
              <div className="flex items-center gap-4">
                <Link to="/terms" className="text-cream-200/70 hover:text-cream-50 transition-colors underline-offset-2 hover:underline">
                  Terms &amp; Conditions
                </Link>
                <Link to="/privacy" className="text-cream-200/70 hover:text-cream-50 transition-colors underline-offset-2 hover:underline">
                  Privacy Policy
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="flex items-center gap-3">
                {/* Visa */}
                <div className="w-9 h-6 bg-cream-100/10 rounded flex items-center justify-center">
                  <span className="text-[8px] font-bold text-cream-100 tracking-wider">VISA</span>
                </div>
                {/* Mastercard */}
                <div className="w-9 h-6 bg-cream-100/10 rounded flex items-center justify-center">
                  <div className="flex -space-x-1">
                    <div className="w-3 h-3 rounded-full bg-[#EB001B]" />
                    <div className="w-3 h-3 rounded-full bg-[#F79E1B]" />
                  </div>
                </div>
                {/* Amex */}
                <div className="w-9 h-6 bg-cream-100/10 rounded flex items-center justify-center">
                  <span className="text-[7px] font-bold text-cream-100">AMEX</span>
                </div>
              </div>
              <span className="hidden sm:flex items-center gap-1.5 text-cream-200/50">
                <Lock className="w-3 h-3" />
                Charges appear as &ldquo;{CARD_DESCRIPTOR}&rdquo;
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
